import React, { useMemo } from 'react'

import colorUtil from 'color'
import PropTypes from 'prop-types'
import { ascend, prop, sortWith } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { Skeleton } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { idAndNameShape } from '~/src/Lib/PropTypes'
import Select from '~/src/UI/Shared/Select'
import T from '~/src/UI/Shared/Typography'

import { EMPTY_ARRAY } from '../Lib/Utils'

const useStyles = makeStyles(theme => ({
  root: {
    '@media print': {
      '& button *, & input': {
        fontSize: theme.typography.h3.fontSize,
      },
    },
    [[
      '.aroyaCultivarsDetail & .MuiButtonBase-root.MuiButton-text',
      '.aroya.aroyaCultivarsDetail & .MuiAutocomplete-root .MuiFormControl-root',
    ].join(', ')]: {
      backgroundColor: colorUtil(theme.palette.primary.light).alpha(theme.palette.states.enabled).string(),
      '&:hover': {
        backgroundColor: colorUtil(theme.palette.primary.light).alpha(theme.palette.states.hover).string(),
      },
    },
    [[
      '.aroyaCultivarsDetail & .MuiButtonBase-root.MuiButton-text',
      '.aroyaCultivarsDetail & .MuiFilledInput-root .MuiFilledInput-input',
      '.aroyaCultivarsDetail & .MuiFilledInput-root .MuiAutocomplete-endAdornment svg',
    ].join(', ')]: {
      color: theme.palette.primary.light,
    }
  }
}), { name: 'FacilityChooser' })

const facilitySort = sortWith([ascend(prop('label'))])

export const Component = ({
  currentMembership,
  memberships = EMPTY_ARRAY,
  onChange,
}) => {
  const classes = useStyles()
  const options = useMemo(() => facilitySort(memberships.map(membership => ({
    value: { id: membership.id, facility: membership.facility.id },
    label: membership.facility.name ?? 'N/A',
  }))), [memberships])

  const currentValue = currentMembership ? options.find(option => option.value.id === currentMembership.id)?.value : null

  return (
    <Select
      fullWidth
      rectangular
      className={classes.root}
      data-testid="aroya-facility-chooser"
      label="Choose facility"
      name="membership"
      value={currentValue}
      options={options}
      onChange={onChange}
    />
  )
}

Component.propTypes = {
  currentMembership: idAndNameShape.isRequired,
  memberships: PropTypes.arrayOf(idAndNameShape).isRequired,
  onChange: PropTypes.func.isRequired,
}

const Wrapped = React.memo(props => {
  const { currentFacility, currentMembership, me, doFacilitySwitch } = useConnect(
    'selectCurrentFacility',
    'selectCurrentMembership',
    'selectMe',
    'doFacilitySwitch',
  )
  const ready = me && currentMembership
  const content = ready && me.memberships?.length ? (
    <Component
      {...props}
      currentMembership={currentMembership}
      memberships={me?.memberships}
      onChange={doFacilitySwitch}
    />
  ) : (
    <T.Subtitle color="primary">{currentFacility?.name ?? 'My Facility'}</T.Subtitle>
  )
  return ready ? content : <Skeleton variant="rectangular">{content}</Skeleton>
})

export default Wrapped
